/**
 * Server-backed RBAC overrides (GET/PATCH /rbac/menu-access).
 * When null, credoraRoles / menuAccess fall back to bundled defaults.
 */

import { normalizeUserRole } from "./credoraRoles";

let remoteRolePermissions = null;
let remotePathPermissions = null;
let remotePermissionCatalog = null;
let remoteMenuLinkGrants = null;
let remoteRoleOptions = null;

export const RBAC_CHANGED_EVENT = "credora-rbac-changed";

function notifyRbacChanged() {
  window.dispatchEvent(new Event(RBAC_CHANGED_EVENT));
}

export function applyMenuAccessPayload(data) {
  if (!data) {
    return;
  }
  const rolePerms = data.rolePermissions ?? data.role_permissions;
  const pathPerms = data.pathPermissions ?? data.path_permissions;
  remoteRolePermissions =
    rolePerms && typeof rolePerms === "object" && Object.keys(rolePerms).length > 0 ? rolePerms : null;
  remotePathPermissions =
    pathPerms && typeof pathPerms === "object" && Object.keys(pathPerms).length > 0 ? pathPerms : null;
  const catalog = data.permissionCatalog ?? data.permission_catalog;
  if (catalog && typeof catalog === "object" && Object.keys(catalog).length > 0) {
    remotePermissionCatalog = catalog;
  }
  if (Array.isArray(data.roles) && data.roles.length > 0) {
    remoteRoleOptions = data.roles.map((r) => ({
      value: r.value || r.key,
      label: r.label,
      isSystem: r.isSystem ?? r.is_system,
      isActive: r.isActive ?? r.is_active,
    }));
  }
  notifyRbacChanged();
}

function normalizeRoleOptions(roles) {
  return roles.map((r) => ({
    value: r.key || r.value,
    label: r.label,
    isSystem: r.isSystem ?? r.is_system,
    isActive: r.isActive ?? r.is_active,
  }));
}

function roleOptionsEqual(a, b) {
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  return a.every((item, i) => item.value === b[i].value && item.label === b[i].label);
}

export function setRemoteRoleOptions(roles) {
  if (!Array.isArray(roles) || roles.length === 0) {
    if (remoteRoleOptions === null) {
      return;
    }
    remoteRoleOptions = null;
    notifyRbacChanged();
    return;
  }
  const next = normalizeRoleOptions(roles);
  if (roleOptionsEqual(remoteRoleOptions, next)) {
    return;
  }
  remoteRoleOptions = next;
  notifyRbacChanged();
}

export function getRemoteRoleOptions() {
  return remoteRoleOptions;
}

export function setRemoteMenuLinkGrants(grants) {
  remoteMenuLinkGrants = Array.isArray(grants) ? grants : null;
  notifyRbacChanged();
}

export function getRemoteMenuLinkGrants() {
  return remoteMenuLinkGrants || [];
}

function grantPathKey(grant) {
  return grant.pathKey || grant.path_key || "";
}

function grantSubjectType(grant) {
  return grant.subjectType || grant.subject_type;
}

function grantEffect(grant) {
  return grant.effect === "deny" ? "deny" : "allow";
}

/** Most specific first, e.g. loans/pending → loans → "" */
function pathKeyVariants(pathKey) {
  const keys = [];
  let key = pathKey;
  while (key) {
    keys.push(key);
    const idx = key.lastIndexOf("/");
    key = idx >= 0 ? key.slice(0, idx) : "";
  }
  return keys;
}

/** Role has at least one menu-link assignment → sidebar uses assignment mode (unlisted = hidden). */
export function roleUsesMenuAssignmentMode(role) {
  const normalized = normalizeUserRole(role);
  if (!normalized) {
    return false;
  }
  return getRemoteMenuLinkGrants().some(
    (grant) =>
      grantSubjectType(grant) === "role" && normalizeUserRole(grant.role) === normalized
  );
}

export function userUsesMenuAssignmentMode(user) {
  if (!user?.id) {
    return false;
  }
  const userId = String(user.id);
  return getRemoteMenuLinkGrants().some(
    (grant) =>
      grantSubjectType(grant) === "user" &&
      String(grant.userId || grant.user_id) === userId
  );
}

/**
 * Menu-link grant for a path.
 * @returns {boolean|null} true=show, false=hide, null=use permission rules
 */
export function resolveMenuLinkGrant(user, pathKey) {
  if (!pathKey || !user?.role) {
    return null;
  }

  const userId = user.id ? String(user.id) : null;
  const role = normalizeUserRole(user.role);
  const variants = pathKeyVariants(pathKey);

  let userAllow = false;
  let userDeny = false;
  let roleAllow = false;
  let roleDeny = false;

  for (const variant of variants) {
    for (const grant of getRemoteMenuLinkGrants()) {
      if (grantPathKey(grant) !== variant) {
        continue;
      }
      const subjectType = grantSubjectType(grant);
      const effect = grantEffect(grant);

      if (subjectType === "user" && userId && String(grant.userId || grant.user_id) === userId) {
        if (effect === "deny") {
          userDeny = true;
        } else {
          userAllow = true;
        }
      } else if (subjectType === "role" && normalizeUserRole(grant.role) === role) {
        if (effect === "deny") {
          roleDeny = true;
        } else {
          roleAllow = true;
        }
      }
    }
  }

  if (userDeny) {
    return false;
  }
  if (userAllow) {
    return true;
  }
  if (roleDeny) {
    return false;
  }
  if (roleAllow) {
    return true;
  }
  return null;
}

export function clearRemoteMenuAccess() {
  remoteRolePermissions = null;
  remotePathPermissions = null;
  remotePermissionCatalog = null;
  remoteMenuLinkGrants = null;
  remoteRoleOptions = null;
  notifyRbacChanged();
}

export function getRemoteRolePermissions() {
  return remoteRolePermissions;
}

export function getRemotePathPermissions() {
  return remotePathPermissions;
}

export function getRemotePermissionCatalog() {
  return remotePermissionCatalog;
}

export function hasRemoteMenuAccess() {
  return remoteRolePermissions != null && remotePathPermissions != null;
}
